import { useParams, Link } from 'react-router-dom';
import { Check, ArrowLeft } from 'lucide-react';
import SEO from '../components/shared/SEO';

const packageData = {
  bronze: {
    tier: 'bronze',
    name: 'Bronze Ticket',
    price: '300',
    accent: '#CD7F32',
    description: 'Essential dispatch support for new owner-operators and small fleets. We manage load sourcing, booking, and daily coordination so you can focus on safe, on-time deliveries.',
    features: [
      'Dedicated dispatcher (Mon-Fri business hours)',
      'Load search + booking (your lane targets)',
      'Rate confirmations + carrier packets',
      'Basic route planning + pickup/delivery coordination (driver-led)',
      'Document management (RCs, PODs)',
      'Weekly performance summary (loads, revenue)',
    ],
    bestFor: 'Drivers just getting started or running a consistent route',
    includes: [
      'Personal dispatcher assigned to your account',
      'Daily load sourcing based on your preferred lanes',
      'Rate negotiation within market standards',
      'Basic paperwork management',
      'Email support during business hours',
    ],
  },
  silver: {
    tier: 'silver',
    name: 'Silver Ticket',
    price: '400',
    accent: '#C0C0C0',
    description: 'Priority dispatch support with stronger optimization and tighter scheduling. Ideal for operators focused on improving RPM, reducing deadhead miles, and running more efficiently week after week.',
    features: [
      'Everything in Bronze, Plus',
      'Extended hours support',
      'Active rate negotiation aligned with RPM',
      'Multi-stop planning + sequenced support',
      'After-hours text support for live or layover issues',
      'Fuel-stop and toll-aware routing',
      'Weekly KPI report (RPM, deadhead)',
      'Broker/shipper check-ins + updates',
    ],
    bestFor: 'Growing owner-operators focused on efficiency and consistency',
    includes: [
      'Everything in Bronze',
      'Extended support hours (early morning to evening)',
      'Aggressive rate negotiation for higher RPM',
      'Multi-stop load optimization',
      'After-hours emergency text support',
      'Detailed weekly performance analytics',
    ],
  },
  gold: {
    tier: 'gold',
    name: 'Gold Ticket',
    price: '500',
    accent: '#D4AF37',
    description: 'White-glove dispatch service designed for maximum revenue and minimal stress. We handle advanced planning, high-priority booking, and proactive communication—perfect for busy operators and small fleets that want elite-level support.',
    features: [
      'Everything in Silver, Plus',
      'Priority load access with multi-board search',
      'Lane strategy with weekly + seasonal optimization',
      'Rate trend tracking with market summary (bi-weekly)',
      'Accessorial request support (detention, lumper, etc.)',
      'Broker coordination and post-trip follow-up',
      'Monthly performance review + optimization focus',
      'Gold availability is limited for a curated, white-glove service',
    ],
    bestFor: 'Busy operators seeking white-glove service',
    includes: [
      'Everything in Silver',
      'Priority access to premium loads',
      'Seasonal lane strategy planning',
      'Market trend analysis and recommendations',
      'Full accessorial fee recovery support',
      'Dedicated account management',
      'Monthly strategy review calls',
    ],
  },
};

const PackageDetailPage = () => {
  const { tier } = useParams();
  const pkg = packageData[tier];

  if (!pkg) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Package Not Found</h1>
          <Link to="/pricing" className="text-[#D4AF37] hover:underline">
            View All Packages
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={`package-detail-${tier}`}>
      <SEO 
        title={`${pkg.name} - $${pkg.price}/week`}
        description={pkg.description}
        canonical={`https://queenblogistics.com/pricing/${tier}`}
        keywords={`${tier} dispatch package, $${pkg.price} weekly dispatch, owner operator dispatch service, trucking dispatch ${tier}`}
      />
      {/* Page Header */}
      <section className="page-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link 
            to="/pricing" 
            className="inline-flex items-center gap-2 text-white/60 hover:text-[#D4AF37] transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Pricing
          </Link>
          
          <div className="flex items-center gap-4 mb-6">
            <span className="text-4xl" style={{ color: pkg.accent }}>&#9733;</span>
            <div className="limited-badge" style={{ borderColor: pkg.accent, color: pkg.accent }}>
              {pkg.tier.toUpperCase()} PACKAGE
            </div>
          </div>
          
          <h1 className="font-['Barlow_Condensed'] text-5xl md:text-6xl lg:text-7xl font-black text-white uppercase">
            {pkg.name.split(' ')[0]}<br />
            <span style={{ color: pkg.accent }}>Ticket</span>
          </h1>
        </div>
      </section>

      {/* Package Details */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <div className="mb-12">
                <h2 className="font-['Barlow_Condensed'] text-2xl font-bold text-white uppercase mb-4">
                  Overview
                </h2>
                <p className="text-white/70 text-lg leading-relaxed">
                  {pkg.description}
                </p>
              </div>

              <div className="mb-12">
                <h2 className="font-['Barlow_Condensed'] text-2xl font-bold text-white uppercase mb-6">
                  What's Included
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {pkg.includes.map((item, index) => (
                    <div 
                      key={index}
                      className="flex items-start gap-3 p-4 bg-[#121212] border border-white/5"
                    >
                      <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: pkg.accent }} />
                      <span className="text-white/70">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h2 className="font-['Barlow_Condensed'] text-2xl font-bold text-white uppercase mb-6">
                  All Features
                </h2>
                <ul className="space-y-4">
                  {pkg.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <Check className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: pkg.accent }} />
                      <span className="text-white/70">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Sidebar */}
            <div>
              <div 
                className="sticky top-24 p-8 bg-[#121212] border-t-4"
                style={{ borderColor: pkg.accent }}
              >
                {/* Ticket Visual */}
                <div className="text-center mb-8 pb-8 border-b border-white/10">
                  <div 
                    className="inline-flex items-center gap-2 px-4 py-2 mb-4"
                    style={{ backgroundColor: `${pkg.accent}20`, border: `1px solid ${pkg.accent}` }}
                  >
                    <span style={{ color: pkg.accent }}>&#9733;</span>
                    <span 
                      className="font-['Barlow_Condensed'] text-sm uppercase tracking-wider"
                      style={{ color: pkg.accent }}
                    >
                      {pkg.tier} Ticket
                    </span>
                  </div>
                  <div className="flex items-baseline justify-center gap-2">
                    <span 
                      className="font-['Barlow_Condensed'] text-6xl font-bold"
                      style={{ color: pkg.accent }}
                    >
                      ${pkg.price}
                    </span>
                    <span className="text-white/50">/weekly</span>
                  </div>
                </div>

                {/* Best For */}
                <div className="mb-8">
                  <p className="text-white/50 text-sm uppercase tracking-wider mb-2">Best For:</p>
                  <p className="text-white">{pkg.bestFor}</p>
                </div>

                {/* CTA */}
                <Link
                  to="/intake"
                  className="block w-full py-4 text-center font-bold uppercase tracking-wider text-sm transition-colors"
                  style={{ 
                    backgroundColor: pkg.accent, 
                    color: '#050505',
                  }}
                  data-testid={`package-cta-${tier}`}
                >
                  Apply for This Plan
                </Link>

                <p className="text-white/40 text-xs text-center mt-4">
                  No long-term contract required
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PackageDetailPage;
